import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { authApi } from '@/utils/api'
import {
  clearTokens,
  getAccessToken,
  setTokens,
} from '@/utils/auth-storage'
import type { AuthResponse, AuthUser } from '@/types'

interface AuthContextValue {
  user: AuthUser | null
  initializing: boolean
  loading: boolean
  error?: string | null
  sendLoginPin: (email: string) => Promise<void>
  verifyLoginPin: (payload: { email: string; pin: string }) => Promise<void>
  loginWithPassword: (payload: {
    phoneNumber: string
    password: string
  }) => Promise<void>
  loginWithEmailPassword: (payload: {
    email: string
    password: string
  }) => Promise<void>
  sendOtp: (phoneNumber: string) => Promise<void>
  verifyOtp: (payload: {
    phoneNumber: string
    otp: string
  }) => Promise<{ needsPassword?: boolean; userId?: string }>
  createPassword: (payload: {
    userId: string
    password: string
    confirmPassword: string
  }) => Promise<void>
  refreshProfile: () => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAuthSuccess = useCallback((data: AuthResponse) => {
    if (!data?.access_token) return
    setTokens(data.access_token, data.refresh_token)
    setUser(data.user)
  }, [])

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await authApi.profile()
      const profileData = (profile as { data?: AuthUser }).data || profile
      setUser(profileData as AuthUser)
    } catch {
      clearTokens()
      setUser(null)
    }
  }, [])

  const sendLoginPin = useCallback(async (email: string) => {
    setLoading(true)
    setError(null)
    try {
      await authApi.sendLoginPin(email)
    } catch (err) {
      setError((err as Error)?.message || 'Unable to send login PIN')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const verifyLoginPin = useCallback(
    async (payload: { email: string; pin: string }) => {
      setLoading(true)
      setError(null)
      try {
        const res = await authApi.verifyLoginPin(payload)
        handleAuthSuccess(res)
      } catch (err) {
        setError((err as Error)?.message || 'Invalid or expired PIN')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleAuthSuccess]
  )

  const loginWithPassword = useCallback(
    async (payload: { phoneNumber: string; password: string }) => {
      setLoading(true)
      setError(null)
      try {
        const res = await authApi.loginWithPassword(payload)
        handleAuthSuccess(res)
      } catch (err) {
        setError((err as Error)?.message || 'Login failed')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleAuthSuccess]
  )

  const loginWithEmailPassword = useCallback(
    async (payload: { email: string; password: string }) => {
      setLoading(true)
      setError(null)
      try {
        const res = await authApi.loginWithEmailPassword(payload)
        handleAuthSuccess(res)
      } catch (err) {
        setError((err as Error)?.message || 'Login failed')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleAuthSuccess]
  )

  const sendOtp = useCallback(async (phoneNumber: string) => {
    setLoading(true)
    setError(null)
    try {
      await authApi.sendOtp(phoneNumber)
    } catch (err) {
      setError((err as Error)?.message || 'Unable to send OTP')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const verifyOtp = useCallback(
    async (payload: { phoneNumber: string; otp: string }) => {
      setLoading(true)
      setError(null)
      try {
        const res = await authApi.verifyOtp(payload)
        const data =
          (res as { data?: { needsPassword?: boolean; userId?: string } }).data ||
          (res as { needsPassword?: boolean; userId?: string })
        const result: { needsPassword?: boolean; userId?: string } = { needsPassword: data?.needsPassword ?? false }
        if (data?.userId) result.userId = data.userId
        return result
      } catch (err) {
        setError((err as Error)?.message || 'Invalid or expired OTP')
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const createPassword = useCallback(
    async (payload: {
      userId: string
      password: string
      confirmPassword: string
    }) => {
      setLoading(true)
      setError(null)
      try {
        const res = await authApi.createPassword(payload)
        handleAuthSuccess(res)
      } catch (err) {
        setError((err as Error)?.message || 'Unable to create password')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleAuthSuccess]
  )

  const logout = useCallback(() => {
    clearTokens()
    setUser(null)
  }, [])

  useEffect(() => {
    const bootstrap = async () => {
      const accessToken = getAccessToken()
      if (!accessToken) {
        setInitializing(false)
        return
      }
      await refreshProfile()
      setInitializing(false)
    }
    void bootstrap()
  }, [refreshProfile])

  const value = useMemo(
    () => ({
      user,
      initializing,
      loading,
      error,
      sendLoginPin,
      verifyLoginPin,
      loginWithPassword,
      loginWithEmailPassword,
      sendOtp,
      verifyOtp,
      createPassword,
      refreshProfile,
      logout,
    }),
    [
      user,
      initializing,
      loading,
      error,
      sendLoginPin,
      verifyLoginPin,
      loginWithPassword,
      loginWithEmailPassword,
      sendOtp,
      verifyOtp,
      createPassword,
      refreshProfile,
      logout,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
